import React, { useCallback, useEffect, useRef, useState } from 'react';

import { get, noop } from 'lodash';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { RedemptionModal } from 'components/offer-redemption-modal/redemption-modal';
import { PromotionType, useCreateOrderlessTransactionMutation } from 'generated/graphql-gateway';
import { OfferType } from 'generated/rbi-graphql';
import {
  IOfferDiscount,
  ISystemwideOffer,
  useFeatureEarningCalculationQuery,
} from 'generated/sanity-graphql';
import { useBatteryLevel } from 'hooks/use-battery-level';
import { useDialogModal } from 'hooks/use-dialog-modal';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { OFFERS } from 'pages/loyalty/loyalty-constants';
import { parseIncentiveData } from 'pages/loyalty/loyalty-incentives-components/incentive-card/parse-incentive-data';
import { IMenuItemDescriptor } from 'pages/loyalty/loyalty-incentives-components/incentive-details/types';
import {
  getMenuItemDescriptorForIncentive,
  makeUrlForIncentive,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/utils';
import { ModalRewardItemUnavailable } from 'pages/loyalty/loyalty-incentives-components/modal-reward-item-unavailable-modal';
import { OfferUpsellModal } from 'pages/loyalty/loyalty-incentives-components/offer-upsell-modal';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { reduceOffers } from 'pages/loyalty/loyalty-offers/utils';
import { LoyaltyReadyToRedeemModal } from 'pages/loyalty/loyalty-ready-to-redeem-modal';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { LoyaltyIdentificationCodeTypeVariation } from 'state/launchdarkly/variations';
import { useLocationContext } from 'state/location';
import { useLoyaltyContext } from 'state/loyalty';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import {
  ICartEntryType,
  ICreateCartEntryParams,
} from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart';
import { canStackReward } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart/utils';
import {
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  isDiscountReward,
  isLoyaltyOffer,
  isReward,
  isSurpriseOffer,
} from 'state/loyalty/types';
import {
  filterRedeemableUpsellOffers,
  getCanStackOfferCheck,
  isDiscountLoyaltyOffer,
} from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';
import { isMobile } from 'utils/environment';
import { logger } from 'utils/logger';
import { PerformancePages, pageUnloaded } from 'utils/render-performance';
import { routes } from 'utils/routing';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';
import { SessionStorage, SessionStorageKeys } from 'utils/session-storage';
import { toast } from 'utils/toast';

import { AddToCartArgs, Incentive, RedemptionMethod } from './types';

/**
 * Wraps the param Component in another component in order to add the function
 * addIncentiveToCart which takes an incentive and a redemption type and adds it to the loyaltyContext
 * It also handles and shows the related modals for each situation.
 * Eg: Shows "only one coupon allowed" if you already have an Offer in the cart.
 *
 * @param Component The component you want to wrap with this logic
 */
export const withAddIncentiveToCart = (Component: React.FC<any>) => {
  return (props: any) => {
    // Utils
    const { formatMessage } = useIntl();
    const { setOriginLocation, user } = useAuthContext();
    const { navigate, location, setStoreLocatorCallbackUrl, pathMatches } = useLocationContext();
    const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
    const loyaltyEnabled = useIsLoyaltyEnabled();
    const loyaltyIdentificationCodeType = useFlag<LoyaltyIdentificationCodeTypeVariation>(
      LaunchDarklyFlag.LOYALTY_IDENTIFICATION_CODE_TYPE
    );
    const enableStaticMenu = useFlag(LaunchDarklyFlag.ENABLE_STATIC_MENU);
    const enableCooldownToast = useFlag(LaunchDarklyFlag.ENABLE_COOLDOWN_TOAST_INFO);
    const enableImprovedSimplifiedRedeemInRestaurantStaticOffers = useFlag(
      LaunchDarklyFlag.ENABLE_IMPROVED_SIMPLIFIED_REDEEM_IN_RESTAURANT_STATIC_OFFERS
    );
    const enableLoyaltyFloatingCartButton = useFlag(
      LaunchDarklyFlag.ENABLE_LOYALTY_FLOATING_CART_BUTTON
    );
    const enableImprovePreselectionDesign = useFlag(
      LaunchDarklyFlag.ENABLE_IMPROVE_PRESELECTION_DESIGN
    );
    const isUsingLoyaltyDrawer =
      (enableLoyaltyFloatingCartButton || enableImprovePreselectionDesign) && isMobile();

    const { trackEvent } = useCdpContext();
    const { pathname } = useLocation();
    const isOffersPage = pathMatches(`.*${routes.offers}.*`);
    const rewardsPath = pathMatches(`${routes.rewardsDetails}.*`)
      ? routes.rewardsDetails
      : routes.rewardsList;

    // State
    const [incentiveName, setIncentiveName] = useState<string>('');
    const [selectedIncentive, setSelectedIncentive] = useState<Incentive>();
    const [showOfferRedemptionModal, setShowOfferRedemptionModal] = useState(false);
    const [rewardLimitPerOrder, setRewardLimitPerOrder] = useState<number | undefined | null>(0);
    const [showUpsellOffer, setShowUpsellOffer] = useState(false);
    const upsellOfferDiscarded = useRef(false);

    // Context
    const { engineRewardsMap } = useLoyaltyRewardsList();
    const { noStoreSelected } = useStoreContext();
    const { setSelectedStaticMenuItem } = useMenuContext();
    const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
    const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
    const unauthenticatedOffers = useAppSelector(selectors.loyalty.selectOffers);
    const loyaltyCmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
    const dispatch = useAppDispatch();
    const { serviceMode } = useServiceModeContext();
    const { removeFromCart } = useOrderContext();
    const { refetchLoyaltyUser } = useLoyaltyContext();
    const appliedLoyaltyRewardsArray = useAppSelector(
      selectors.loyalty.selectAppliedLoyaltyRewardsArray
    );

    const {
      inRestaurantRedemptionCart,
      addInRestaurantRedemptionEntry,
      shouldForceRestaurantSelection,
      clearInRestaurantRedemptionAllRewards,
      isOfferRequiresUserAuthenticationRuleEnabled,
      removeInRestaurantRedemptionEntry,
    } = useInRestaurantRedemptionContext();
    const loyaltyUser = useAppSelector(selectors.loyalty.selectUser);
    const [InvalidIncentiveModal, openInvalidIncentiveModal] = useDialogModal({
      modalAppearanceEventMessage: 'Invalid In Restaurant Incentive Redemption',
    });
    const [createOrderlessTransaction] = useCreateOrderlessTransactionMutation({
      variables: {
        input: {
          loyaltyId: loyaltyUser?.id,
          channel: 'ORDERLESS',
          incentives: [{ id: selectedIncentive?.loyaltyEngineId || '', type: PromotionType.OFFER }],
        },
      },
      onCompleted: () => {
        refetchLoyaltyUser();
        setShowOfferRedemptionModal(true);
      },
      onError: error => {
        let title = null;
        let message = formatMessage({ id: 'errorCreateOrder' });

        if (error?.graphQLErrors?.[0]?.extensions?.code === 'RulesError') {
          title = formatMessage({ id: 'offersCooldownTitle' });
          message = formatMessage(
            { id: 'offersCooldownInfo' },
            {
              period: earningCalculationData?.EarningCalculation?.offerRedemptionCooldownPeriod,
            }
          );
        }
        openInvalidIncentiveModal({ title, message });
      },
    });

    const { data: earningCalculationData } = useFeatureEarningCalculationQuery({
      variables: { id: 'earningCalculation' },
    });

    const standardOffersLimit =
      earningCalculationData?.EarningCalculation?.offerRedemptionLimits?.standardOffersLimit || 1;

    const { isLimitRewardRedemptionPerOrderReached, rewardLimitePerOrder } =
      useLoyaltyRewards(loyaltyUser);

    const { isAuthenticated } = useAuthContext();

    const diffInMin = getDifferenceToNow(
      DIFFERENCE_TIME_UNITS.MINUTES,
      loyaltyUser?.offerRedemptionAvailability?.availableAfter || ''
    );

    const showOfferCooldownToast = () => {
      //If the diffInMin is greater than 0 it means that the user already have an offer in course so we do nothing.
      if (!enableCooldownToast || diffInMin > 0 || !serviceMode) {
        return;
      }

      if (earningCalculationData?.EarningCalculation?.offerRedemptionCooldownPeriod) {
        toast.default(
          formatMessage(
            { id: 'offersCooldownInfo' },
            {
              period: earningCalculationData?.EarningCalculation?.offerRedemptionCooldownPeriod,
            }
          )
        );
      }
    };

    const userBatteryLevel = useBatteryLevel();

    const redeemableUpsellOffers = useCallback(
      (offer: Incentive | undefined) => {
        const [filteredOffers] = reduceOffers({
          cmsOffers: loyaltyCmsOffers,
          personalizedOffers: [],
          baseOffers: loyaltyUser?.id ? userOffers : unauthenticatedOffers,
          appliedOffers,
          inRestaurantRedemptionCart,
          checkLoyaltyOfferAvailability,
          enableSortOffersForServiceMode: false,
          setOffersFeedbackMap: noop,
          userOfferAttributes: {
            userBatteryLevel,
            dobDeleted: user?.details.dobDeleted,
            phoneNumberVerified: user?.details.phoneVerified,
            promotionalEmails: user?.details.promotionalEmails,
            zipcode: user?.details.zipcode,
            hasDeliveryAddress: !!user?.details.deliveryAddresses?.length,
            favoriteStores: user?.details.favoriteStores?.map(store => store.storeNumber),
            favoriteOffers: user?.details.favoriteOffers?.map(offerObj => offerObj.id),
          },
        });
        return filterRedeemableUpsellOffers(offer as LoyaltyOffer, filteredOffers);
      },
      [
        appliedOffers,
        checkLoyaltyOfferAvailability,
        inRestaurantRedemptionCart,
        loyaltyCmsOffers,
        loyaltyUser?.id,
        unauthenticatedOffers,
        user?.details,
        userBatteryLevel,
        userOffers,
      ]
    );

    // Dialogs
    const [ReplaceOfferDialog, openReplaceOfferDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Replace Coupon',
      onConfirm: (data: { args: AddToCartArgs }) => {
        if (inRestaurantRedemptionCart.length >= standardOffersLimit) {
          const firstOfferInCart = inRestaurantRedemptionCart.find(
            item => item.type === ICartEntryType.OFFER
          );
          removeInRestaurantRedemptionEntry(firstOfferInCart!);
        }
        redeemIncentive(data.args);
      },
      showCancel: true,
    });
    const [ReplaceRewardDialog, openReplaceRewardDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Replace Reward',
      showCancel: true,
      onConfirm: (data: {
        args: AddToCartArgs;
        incentiveMenuPath?: string;
        isCartDiscount?: boolean;
      }) => onConfirmReplaceReward(data.args, data.incentiveMenuPath, data.isCartDiscount),
    });
    const [ItemUnavailableModal, openItemUnavailableModal] = useDialogModal<
      {},
      { rewardName: string }
    >({
      Component: ModalRewardItemUnavailable,
    });
    const [ReadyToRedeemDialog, openReadyToRedeemDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Ready to Redeem',
      Component: enableImprovedSimplifiedRedeemInRestaurantStaticOffers
        ? LoyaltyReadyToRedeemModal
        : undefined,
      onConfirm: () => {
        if (selectedIncentive && isLoyaltyOffer(selectedIncentive)) {
          dispatch(actions.loyalty.setSelectedOffer(selectedIncentive));

          const name = selectedIncentive.name;
          trackEvent({
            name: 'Legacy Offer Added to In Store Order',
            type: EventTypes.Other,
            attributes: {
              sanityId: selectedIncentive._id || '',
              name: name ? getFirstStringInLocaleBlockContent(name) : '',
            },
          });
          trackEvent({
            name: CustomEventNames.OFFER_ADDED_TO_ORDER,
            type: EventTypes.Other,
            attributes: {
              engineId: selectedIncentive?.loyaltyEngineId ?? '',
              sanityId: selectedIncentive?._id ?? '',
              name: name ? getFirstStringInLocaleBlockContent(name) : '',
              redemptionMode: 'In-Store',
            },
          });
        }

        if (loyaltyUser?.id && selectedIncentive?.loyaltyEngineId) {
          createOrderlessTransaction();
        } else {
          setShowOfferRedemptionModal(true);
        }
        if (isOffersPage) {
          showOfferCooldownToast();
        }
      },
      showCancel: enableImprovedSimplifiedRedeemInRestaurantStaticOffers,
    });
    const {
      SwitchCartMethodDialog,
      shouldShowSwitchToMobileDialog,
      shouldShowSwitchToStoreDialog,
      openSwitchCartMethod,
    } = useSwitchCartMethodDialog();

    // Handle add to cart flow
    const addIncentiveToCart = (args: AddToCartArgs) => {
      setSelectedIncentive(args.incentive);
      if (isLoyaltyOffer(args.incentive)) {
        const canStackOfferToMobile = getCanStackOfferCheck((of: LoyaltyAppliedOffer) => of);
        const canStackOfferToInRestaurant = getCanStackOfferCheck(entry =>
          //@ts-ignore
          get(entry, 'details.offer')
        );

        const offersInRestaurantCart = inRestaurantRedemptionCart.filter(
          cartItem => cartItem.type === ICartEntryType.OFFER
        );

        const showReplaceModal =
          !canStackOfferToMobile(args.incentive, appliedOffers, standardOffersLimit) ||
          !canStackOfferToInRestaurant(args.incentive, offersInRestaurantCart, standardOffersLimit);

        if (showReplaceModal) {
          return openReplaceOfferDialog({ args });
        }
      }

      redeemIncentive(args);
    };

    const checkUpSellOffers = (args: AddToCartArgs) => {
      if (
        isLoyaltyOffer(args.incentive) &&
        !upsellOfferDiscarded.current &&
        args.incentive?.upsellOptions?.length
      ) {
        if (redeemableUpsellOffers(args.incentive)?.length) {
          // Show upsell modal if at least 1 upsell offer can be shown
          setShowUpsellOffer(true);
          return true;
        }
      }

      return false;
    };

    const redeemIncentive = (args: AddToCartArgs) => {
      if (args.method === RedemptionMethod.MOBILE) {
        if (checkUpSellOffers(args)) {
          return;
        }
        return handleMobileRedemption(args);
      }
      tryInRestaurantRedemption(args);
    };

    const tryInRestaurantRedemption = (args: AddToCartArgs) => {
      if (shouldShowSwitchToStoreDialog) {
        openSwitchCartMethod({
          onConfirm: () => handleInRestaurantRedemption(args),
        });
      } else {
        handleInRestaurantRedemption(args);
      }
    };

    const handleMobileRedemption = (args: AddToCartArgs) => {
      if (shouldShowSwitchToMobileDialog) {
        openSwitchCartMethod({ onConfirm: () => mobileRedemption(args) });
      } else {
        mobileRedemption(args);
      }
    };

    const handleInRestaurantRedemption = (args: AddToCartArgs) => {
      const shouldRedeemInRestaurant = isReward(args.incentive)
        ? loyaltyEnabled
        : loyaltyIdentificationCodeType === LoyaltyIdentificationCodeTypeVariation.DYNAMIC;

      if (shouldRedeemInRestaurant) {
        if (checkUpSellOffers(args)) {
          return;
        }
        if (!isAuthenticated()) {
          if (
            isOfferRequiresUserAuthenticationRuleEnabled(args.incentive as LoyaltyOffer) === false
          ) {
            openReadyToRedeemDialog();
            return;
          } else {
            // add this logic only to Offers
            if (isOffersPage) {
              setOriginLocation(pathname);
              navigate(routes.signUp);
              return;
            }
          }
        }
        // If user is not enrolled in loyalty continue with old flow
        if (!loyaltyUser?.id) {
          openReadyToRedeemDialog();
          return;
        }
        inRestaurantRedemption(args);
      } else {
        // offerHasRequiresUserAuthenticationRuleEnabled must be different from false, because
        // when it is true or undefined, the user must log in to redeem.
        if (
          !isAuthenticated() &&
          isOfferRequiresUserAuthenticationRuleEnabled(args.incentive as LoyaltyOffer) !== false
        ) {
          // add this logic only to Offers
          if (isOffersPage) {
            setOriginLocation(pathname);
            navigate(routes.signUp);
          } else {
            navigate(formatMessage({ id: 'routes.redemptionInRestaurant' }));
          }
          return;
        }
        if (checkUpSellOffers(args)) {
          return;
        }
        openReadyToRedeemDialog();
      }
    };

    const createInRestaurantCartEntry = useCallback(
      (incentive: AddToCartArgs['incentive']) => {
        let inRestaurantCartEntry: ICreateCartEntryParams | undefined;
        if (!isReward(incentive)) {
          inRestaurantCartEntry = { offer: incentive };
        } else {
          const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
          if (engineReward) {
            inRestaurantCartEntry = {
              reward: incentive,
              engineReward,
            };
          }
        }
        return inRestaurantCartEntry;
      },
      [engineRewardsMap]
    );

    const mobileRedemption = (args: AddToCartArgs) => {
      const { incentive } = args;
      let discountItemDescriptor: IMenuItemDescriptor | undefined;

      if (isLoyaltyOffer(incentive)) {
        dispatch(actions.loyalty.setSelectedOffer(incentive));

        if (isDiscountLoyaltyOffer(incentive)) {
          // If limit of offers reached remove the first one
          if (appliedOffers?.length >= standardOffersLimit) {
            removeFromCart({ cartId: appliedOffers[0].cartId });
          }

          const discountIncentive = incentive?.incentives?.[0] as IOfferDiscount;

          // If the discount is not for a specific item we apply it to the cart
          if (!discountIncentive?.discountProduct) {
            // Cart discount offers should not show menu item details
            dispatch(
              actions.loyalty.applyOffer({
                id: incentive.loyaltyEngineId,
                type: OfferType.GLOBAL,
                isStackable: incentive.isStackable,
                isSurprise: isSurpriseOffer(incentive),
                cmsId: incentive._id,
                cartId: 'discount-offer',
              })
            );
            toast.success(formatMessage({ id: 'offerAddedToCart' }));
            const name = incentive.name;
            trackEvent({
              name: CustomEventNames.OFFER_ADDED_TO_ORDER,
              type: EventTypes.Other,
              attributes: {
                engineId: incentive?.loyaltyEngineId ?? '',
                sanityId: incentive?._id ?? '',
                name: name ? getFirstStringInLocaleBlockContent(name) : '',
                redemptionMode: 'Online',
              },
            });
            navigate('/menu');
            return;
          }

          discountItemDescriptor = {
            _id: discountIncentive.discountProduct._id,
            _type: discountIncentive.discountProduct._type,
          };
        }

        if (isOffersPage) {
          showOfferCooldownToast();
        }
      }

      if (isDiscountReward(incentive) && !isLoyaltyOffer(incentive)) {
        const discountIncentive = incentive?.incentives?.[0] as IOfferDiscount;

        // If the discount is not for a specific item we apply it to the cart
        if (!discountIncentive?.discountProduct) {
          if (isLimitRewardRedemptionPerOrderReached) {
            return openReplaceRewardDialog({ args, isCartDiscount: true });
          }
          dispatch(
            actions.loyalty.applyReward({
              cartId: 'discount-offer',
              rewardBenefitId: discountIncentive?._id!,
              quantity: 1,
            })
          );

          navigate(routes.cart);
          return;
        }

        discountItemDescriptor = {
          _id: discountIncentive.discountProduct._id,
          _type: discountIncentive.discountProduct._type,
        };
      }

      const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
      const menuItemDescriptor =
        discountItemDescriptor ??
        getMenuItemDescriptorForIncentive(incentive, engineReward?.rewardBenefitId);
      const incentiveMenuPath = makeUrlForIncentive(incentive, menuItemDescriptor);
      if (!menuItemDescriptor || !incentiveMenuPath) {
        logger.warn(
          `Attempting to make a url for reward that is missing data: reward ${incentive._id}`
        );
        const incentiveData = parseIncentiveData(incentive);
        setIncentiveName(incentiveData.name);
        openItemUnavailableModal();
        return;
      }

      // Save incentive data before redirecting to StoreLocator
      if (noStoreSelected && !enableStaticMenu) {
        const searchIndex = incentiveMenuPath.indexOf('?');
        const search = searchIndex !== -1 ? incentiveMenuPath.slice(searchIndex) : '';
        setSelectedStaticMenuItem({
          itemId: `${menuItemDescriptor._id}-${menuItemDescriptor._type}`,
          itemName: incentiveName || '',
          search,
        });
      }

      if (isReward(incentive) && isLimitRewardRedemptionPerOrderReached) {
        return openReplaceRewardDialog({ args, incentiveMenuPath });
      }

      navigate(incentiveMenuPath, { state: { isFromSurprise: location.pathname === routes.cart } });
    };

    const inRestaurantRedemption = (args: AddToCartArgs) => {
      const { incentive } = args;
      const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
      const shouldSelectStore = shouldForceRestaurantSelection && noStoreSelected;
      if (shouldSelectStore) {
        setStoreLocatorCallbackUrl(location.pathname);
        navigate(formatMessage({ id: 'routes.storeLocator' }));
      } else if (!isReward(incentive)) {
        addInRestaurantRedemptionEntry({ offer: incentive });
        const nextRoute = isUsingLoyaltyDrawer ? routes.rewardsOffers : routes.redeem;
        navigate(nextRoute);
      } else if (engineReward) {
        if (!canStackReward(inRestaurantRedemptionCart, rewardLimitPerOrder)) {
          return openReplaceRewardDialog({ args });
        }
        addInRestaurantRedemptionEntry({
          reward: incentive,
          engineReward,
        });
        const nextRoute = isUsingLoyaltyDrawer ? rewardsPath : routes.redeem;
        navigate(nextRoute);
      }
      if (isOffersPage) {
        showOfferCooldownToast();
      }
    };

    const onConfirmReplaceReward = useCallback(
      (args: AddToCartArgs, incentiveMenuPath?: string, isCartDiscount?: boolean) => {
        const { incentive, method } = args;

        if (method === RedemptionMethod.IN_RESTAURANT) {
          const inRestaurantCartEntry = createInRestaurantCartEntry(incentive);
          if (inRestaurantCartEntry) {
            if (!rewardLimitePerOrder || rewardLimitePerOrder === 1) {
              // If no reward limit set or limit is one, clear all rewards
              clearInRestaurantRedemptionAllRewards();
            } else {
              // If reward limit is bigger than 1, remove eldest reward first
              const firstRewardInCart = inRestaurantRedemptionCart.find(
                item => item.type === ICartEntryType.REWARD
              );
              removeInRestaurantRedemptionEntry(firstRewardInCart!);
            }
            addInRestaurantRedemptionEntry(inRestaurantCartEntry);

            const nextRoute = isUsingLoyaltyDrawer ? routes.rewardsList : routes.redeem;
            return navigate(nextRoute, { replace: true });
          }
        }

        // get the first item to replace, in case more than one is available
        const itemToReplace = appliedLoyaltyRewardsArray && appliedLoyaltyRewardsArray[0];

        if (itemToReplace) {
          dispatch(
            actions.loyalty.removeAppliedReward({
              rewardBenefitId: itemToReplace.rewardId,
              cartId: itemToReplace.cartId,
            })
          );

          removeFromCart({ cartId: itemToReplace.cartId });
        }

        if (isCartDiscount) {
          const discountIncentive = incentive?.incentives?.[0] as IOfferDiscount;
          dispatch(
            actions.loyalty.applyReward({
              cartId: 'discount-offer',
              rewardBenefitId: discountIncentive?._id!,
              quantity: 1,
            })
          );
          navigate(routes.cart);
          return;
        }

        if (incentiveMenuPath) {
          navigate(incentiveMenuPath, {
            state: { isFromSurprise: location.pathname === routes.cart },
          });
        } else {
          navigate(routes.redeem, { replace: true });
        }
      },
      [
        addInRestaurantRedemptionEntry,
        dispatch,
        navigate,
        location.pathname,
        removeFromCart,
        appliedLoyaltyRewardsArray,
        createInRestaurantCartEntry,
        clearInRestaurantRedemptionAllRewards,
      ]
    );

    const onDismissRestaurantRedemptionModal = () => {
      pageUnloaded(PerformancePages.OFFER_REDEMPTION);
      SessionStorage.removeItem(SessionStorageKeys.REDEMPTION_START_TIME);

      setShowOfferRedemptionModal(false);
      navigate(`${formatMessage({ id: 'routes.loyaltyHome' })}/${OFFERS}`);
    };

    const handleUpsellOfferSelect = useCallback(
      (offer: ISystemwideOffer) => {
        setShowUpsellOffer(false);
        navigate(`${routes.rewardsOffers}/${offer?.loyaltyEngineId}`);
      },
      [navigate]
    );

    const handleUpsellOfferDiscard = useCallback(() => {
      setShowUpsellOffer(false);
      upsellOfferDiscarded.current = true;
    }, [setShowUpsellOffer]);

    useEffect(() => {
      setRewardLimitPerOrder(
        earningCalculationData?.EarningCalculation?.rewardsRedemptionConfig?.standardRewardsLimit
      );
    }, [earningCalculationData]);

    return (
      <>
        <ReplaceOfferDialog
          heading={formatMessage({ id: 'offersLimitReached' }, { limit: standardOffersLimit })}
          body={formatMessage({ id: 'replaceOffer' })}
          buttonLabel={formatMessage({ id: 'yes' })}
          cancelLabel={formatMessage({ id: 'no' })}
        />
        <ReplaceRewardDialog
          heading={formatMessage({ id: 'replaceReward' }, { limit: rewardLimitPerOrder })}
          body={formatMessage({ id: 'replaceRewardText' })}
          buttonLabel={formatMessage({ id: 'replaceItem' })}
          cancelLabel={formatMessage({ id: 'dontReplace' })}
        />
        <SwitchCartMethodDialog />
        {showUpsellOffer ? (
          <OfferUpsellModal
            initialOfferId={selectedIncentive?._id!}
            upsellOptions={redeemableUpsellOffers(selectedIncentive)}
            onSelect={handleUpsellOfferSelect}
            onDismiss={handleUpsellOfferDiscard}
          />
        ) : null}
        <ItemUnavailableModal rewardName={incentiveName} />
        {selectedIncentive && isLoyaltyOffer(selectedIncentive) && (
          <>
            <InvalidIncentiveModal heading={formatMessage({ id: 'uhoh' })} showCloseButton />
            <ReadyToRedeemDialog
              data-testid="ready-to-redeem-dialog"
              heading={formatMessage({ id: 'couponWillExpire' })}
              body={formatMessage({ id: 'confirmDialogBody' })}
              buttonLabel={formatMessage({ id: 'redeem' })}
              showCloseButton
            />
          </>
        )}
        {showOfferRedemptionModal && (
          <RedemptionModal
            selectedOffer={selectedIncentive as LoyaltyOffer}
            onDismiss={onDismissRestaurantRedemptionModal}
          />
        )}

        <Component {...props} addIncentiveToCart={addIncentiveToCart} />
      </>
    );
  };
};
